/* eslint-disable react/jsx-props-no-spreading */
import {
  faCircleExclamation,
  faCircleInfo,
  faCircleMinus,
  faCirclePlus,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridColDef } from '@mui/x-data-grid-premium';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { endOfMonth, format, isAfter, isBefore, isSameDay, startOfDay, startOfMonth } from 'date-fns';
import React, { useEffect, useState } from 'react';

import { addCommas, removeCommas } from '../../util/helpers';
import DataGrid from '../shared/DataGrid';

// ----- Types -----
type PeriodType = 'dateRange' | 'months';

interface DeliveryBlock {
  id: string;
  /** Original profile id (number) when this block came from a saved profile, undefined when freshly created */
  originalId?: number | string;
  /** UI mode for the period pickers. Both modes share the same customStartDate/customEndDate. */
  periodType: PeriodType;
  customStartDate?: Date | null;
  customEndDate?: Date | null;
  regularImpressions: string; // text input value
  bonusImpressions: string;
  /** Carry-through fields from the source profile that we don't edit but must preserve */
  preserved?: {
    io_id?: string;
    profile_id?: any;
    dollarBudget?: string | number;
    deliveredImpressions?: string | number;
    disabled?: boolean;
    active?: boolean;
  };
}

export interface CustomDeliveryProfile {
  id?: number | string;
  profile_id?: any;
  io_id?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  bonus?: string | number;
  dollarBudget?: string | number;
  deliveredImpressions?: string | number;
  profileImpressionBudget?: string | number;
  disabled?: boolean;
  active?: boolean;
  [k: string]: any;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (profiles: CustomDeliveryProfile[]) => void;
  /** Existing profiles to seed the draft with (already in form shape) */
  initialProfiles: CustomDeliveryProfile[];
  /** Line item flight start (real cap on dates) */
  lineItemStartDate?: Date | null;
  /** Line item flight end (real cap on dates) */
  lineItemEndDate?: Date | null;
  /** Contract flight start — outer envelope for profile dates */
  contractStartDate?: Date | null;
  /** Contract flight end — outer envelope for profile dates */
  contractEndDate?: Date | null;
  /** Real impression-budget cap from the line item */
  impressionBudgetCap: number;
  /** Real bonus-impressions cap from the line item */
  bonusCap: number;
  /** Line item id, used when creating fresh profiles for io_id */
  lineItemId?: number | string;
}

// ----- Helpers -----
const profileToBlock = (profile: CustomDeliveryProfile): DeliveryBlock => ({
  id: `block-${profile.id ?? Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  originalId: typeof profile.id === 'number' ? profile.id : undefined,
  periodType: 'dateRange',
  customStartDate: profile.startDate ? new Date(profile.startDate) : null,
  customEndDate: profile.endDate ? new Date(profile.endDate) : null,
  regularImpressions:
    profile.profileImpressionBudget != null && profile.profileImpressionBudget !== ''
      ? String(profile.profileImpressionBudget)
      : '',
  bonusImpressions: profile.bonus != null && profile.bonus !== '' ? String(profile.bonus) : '',
  preserved: {
    io_id: profile.io_id,
    profile_id: profile.profile_id,
    dollarBudget: profile.dollarBudget ?? 0,
    deliveredImpressions: profile.deliveredImpressions ?? 0,
    disabled: profile.disabled ?? false,
    active: profile.active ?? false,
  },
});

const blockToProfile = (block: DeliveryBlock, lineItemId?: number | string): CustomDeliveryProfile => {
  let startDate = block.customStartDate ?? null;
  let endDate = block.customEndDate ?? null;

  // In months mode, snap to month boundaries — the picker selects a month/year but stores a Date.
  if (block.periodType === 'months') {
    if (startDate) startDate = startOfMonth(startDate);
    if (endDate) endDate = endOfMonth(endDate);
  }

  const preserved = block.preserved ?? {};

  return {
    id: block.originalId, // undefined for new ones — server will assign
    profile_id: preserved.profile_id ?? '',
    io_id: preserved.io_id ?? String(lineItemId ?? ''),
    startDate,
    endDate,
    bonus: block.bonusImpressions ? Number(removeCommas(block.bonusImpressions)) || 0 : 0,
    dollarBudget: preserved.dollarBudget ?? 0,
    deliveredImpressions: preserved.deliveredImpressions ?? 0,
    profileImpressionBudget: block.regularImpressions ? Number(removeCommas(block.regularImpressions)) || 0 : 0,
    disabled: preserved.disabled ?? false,
    active: preserved.active ?? false,
  };
};

const sortBlocks = (blocks: DeliveryBlock[]): DeliveryBlock[] =>
  [...blocks].sort((a, b) => {
    if (!a.customStartDate) return 1;
    if (!b.customStartDate) return -1;
    return a.customStartDate.getTime() - b.customStartDate.getTime();
  });

// ----- Component -----
function CustomDeliveryProfileModal(props: Props) {
  const {
    open,
    onClose,
    onSave,
    initialProfiles,
    lineItemStartDate,
    lineItemEndDate,
    contractStartDate,
    contractEndDate,
    impressionBudgetCap,
    bonusCap,
    lineItemId,
  } = props;

  const theme = useTheme();

  const [draftBlocks, setDraftBlocks] = useState<DeliveryBlock[]>([]);
  const [modalTab, setModalTab] = useState<0 | 1>(0); // 0=dateRange, 1=months
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [modalTouched, setModalTouched] = useState(false);

  const makeEmptyBlock = (periodType: PeriodType): DeliveryBlock => ({
    id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    periodType,
    customStartDate: null,
    customEndDate: null,
    regularImpressions: '',
    bonusImpressions: '',
  });

  // Seed draft when modal opens
  useEffect(() => {
    if (!open) return;
    if (initialProfiles.length > 0) {
      const seed = initialProfiles.map(profileToBlock);
      setDraftBlocks(seed);
      setModalTouched(true);
    } else {
      setDraftBlocks([makeEmptyBlock('dateRange')]);
      setModalTouched(false);
    }
    setModalTab(0);
    setValidationErrors(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ----- Handlers -----
  const handleClose = () => {
    setValidationErrors(new Set());
    onClose();
  };

  const handleModalTabChange = (newValue: 0 | 1) => {
    setModalTab(newValue);
    const newPeriodType: PeriodType = newValue === 0 ? 'dateRange' : 'months';
    setDraftBlocks([makeEmptyBlock(newPeriodType)]);
    setValidationErrors(new Set());
  };

  const addDeliveryBlock = () => {
    setDraftBlocks((prev) => [...prev, makeEmptyBlock(modalTab === 0 ? 'dateRange' : 'months')]);
  };

  const removeDeliveryBlock = (id: string) => {
    setDraftBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  const updateDeliveryBlock = (id: string, updates: Partial<DeliveryBlock>) => {
    setModalTouched(true);
    const dateFields: (keyof DeliveryBlock)[] = ['customStartDate', 'customEndDate'];
    setDraftBlocks((prev) => {
      const updated = prev.map((b) => (b.id === id ? { ...b, ...updates } : b));
      if (Object.keys(updates).some((k) => dateFields.includes(k as keyof DeliveryBlock))) {
        return sortBlocks(updated);
      }
      return updated;
    });
  };

  const handleImpressionChange = (blockId: string, field: 'regularImpressions' | 'bonusImpressions', value: string) => {
    const cleanValue = String(removeCommas(value));
    updateDeliveryBlock(blockId, { [field]: cleanValue });
  };

  // True if `date` falls within another row's range. For months-mode peers we expand the range
  // to the full month boundaries so picking e.g. "Mar 2026" in row B blocks any day in March
  // for row A's day-level pickers.
  const isDateTakenByOtherRow = (blockId: string, date: Date) =>
    draftBlocks
      .filter((b) => b.id !== blockId && b.customStartDate && b.customEndDate)
      .some((b) => {
        const rawStart = b.customStartDate as Date;
        const rawEnd = b.customEndDate as Date;
        const start = startOfDay(b.periodType === 'months' ? startOfMonth(rawStart) : rawStart);
        const end = startOfDay(b.periodType === 'months' ? endOfMonth(rawEnd) : rawEnd);
        const target = startOfDay(date);
        return (
          (isAfter(target, start) || isSameDay(target, start)) && (isBefore(target, end) || isSameDay(target, end))
        );
      });

  const calculateTotal = (blocks: DeliveryBlock[]) => {
    const totalRegular = blocks.reduce((sum, b) => sum + (parseFloat(b.regularImpressions || '0') || 0), 0);
    const totalBonus = blocks.reduce((sum, b) => sum + (parseFloat(b.bonusImpressions || '0') || 0), 0);
    const regularPct = impressionBudgetCap > 0 ? (totalRegular / impressionBudgetCap) * 100 : 0;
    const bonusPct = bonusCap > 0 ? (totalBonus / bonusCap) * 100 : 0;
    return { totalRegular, totalBonus, regularPct, bonusPct };
  };

  const splitBudgetEvenly = (field: 'regularImpressions' | 'bonusImpressions') => {
    const total = field === 'regularImpressions' ? impressionBudgetCap : bonusCap;
    const emptyBlocks = draftBlocks.filter((b) => !b[field]);
    if (emptyBlocks.length === 0 || total <= 0) return;
    const alreadyAllocated = draftBlocks
      .filter((b) => b[field] && b[field] !== '0')
      .reduce((sum, b) => sum + (parseInt(String(removeCommas(b[field]!)), 10) || 0), 0);
    const remaining = total - alreadyAllocated;
    if (remaining <= 0) return;
    const emptyIds = new Set(emptyBlocks.map((b) => b.id));
    const base = Math.floor(remaining / emptyBlocks.length);
    const remainder = remaining - base * emptyBlocks.length;
    let remainderCount = 0;
    setModalTouched(true);
    setDraftBlocks((prev) =>
      prev.map((block) => {
        if (!emptyIds.has(block.id)) return block;
        const value = base + (remainderCount < remainder ? 1 : 0);
        remainderCount += 1;
        return { ...block, [field]: String(value) };
      })
    );
  };

  const handleSetDelivery = () => {
    const errors = new Set<string>();
    draftBlocks.forEach((block) => {
      if (!block.customStartDate || !block.customEndDate) errors.add(block.id);
    });
    if (errors.size > 0) {
      setValidationErrors(errors);
      return;
    }
    const profiles = draftBlocks.map((b) => blockToProfile(b, lineItemId));
    onSave(profiles);
    setValidationErrors(new Set());
    onClose();
  };

  // ----- Derived -----
  const { totalRegular, totalBonus, regularPct, bonusPct } = calculateTotal(draftBlocks);
  const isOverLimit = regularPct > 100 || bonusPct > 100;

  const allocationColor = (touched: boolean, pct: number) => {
    if (!touched) return theme.palette.text.disabled;
    if (pct > 100) return theme.palette.error.main;
    if (pct < 100) return theme.palette.warning.main;
    return theme.palette.text.secondary;
  };
  const regularColor = allocationColor(modalTouched, regularPct);
  const bonusColor = allocationColor(modalTouched, bonusPct);

  const regularAutofillDisabled = draftBlocks.length > 0 && draftBlocks.every((b) => !!b.regularImpressions);
  const bonusAutofillDisabled = draftBlocks.length > 0 && draftBlocks.every((b) => !!b.bonusImpressions);

  // Borderless input styles
  const fieldStyles = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { border: 'none' },
      '&:hover': {
        bgcolor: theme.palette.grey[50],
        '& fieldset': { border: `1px solid ${theme.palette.divider}` },
      },
      '&.Mui-focused': {
        bgcolor: theme.palette.background.paper,
        '& fieldset': { border: `1px solid ${theme.palette.primary.main}` },
      },
    },
  };

  const datePickerSx = (error: boolean) => ({
    width: 152,
    '& fieldset': {
      border: error ? `1px solid ${theme.palette.error.main} !important` : 'none !important',
    },
    '&:hover': {
      bgcolor: theme.palette.grey[50],
      '& fieldset': {
        border: error
          ? `1px solid ${theme.palette.error.main} !important`
          : `1px solid ${theme.palette.divider} !important`,
      },
    },
    '&:focus-within': {
      bgcolor: theme.palette.background.paper,
      '& fieldset': {
        border: error
          ? `1px solid ${theme.palette.error.main} !important`
          : `1px solid ${theme.palette.primary.main} !important`,
      },
    },
  });

  // ----- Grid rows / columns -----
  const draftRows = draftBlocks.map((block, index) => {
    const next = draftBlocks[index + 1];
    let rowHasGap = false;
    if (next && block.customEndDate && next.customStartDate) {
      // For months mode, the saved profile's end date will be snapped to end-of-month, but the
      // picker may currently hold any day inside the month — compare on the equivalent month-end
      // when in months mode so the gap warning matches the on-save semantics.
      const effectiveEnd = block.periodType === 'months' ? endOfMonth(block.customEndDate) : block.customEndDate;
      const effectiveNextStart =
        next.periodType === 'months' ? startOfMonth(next.customStartDate) : next.customStartDate;
      const dayAfterEnd = new Date(effectiveEnd);
      dayAfterEnd.setDate(dayAfterEnd.getDate() + 1);
      rowHasGap = !isSameDay(dayAfterEnd, effectiveNextStart);
    }
    return { id: block.id, block, rowHasGap };
  });

  const columns: GridColDef[] = [
    {
      field: 'period',
      headerName: 'Custom Flighting Period',
      width: 362,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const { block, rowHasGap } = params.row as { block: DeliveryBlock; rowHasGap: boolean };
        const hasError = validationErrors.has(block.id);
        const isMonths = block.periodType === 'months';
        // Both modes share the same value/onChange wiring; only the picker's views/format differ.
        const sharedPickerProps = isMonths
          ? { views: ['year' as const, 'month' as const], openTo: 'month' as const, format: 'MMM yyyy' }
          : {};
        const clearErrorIfBothSet = (other: Date | null | undefined) => {
          if (other) {
            setValidationErrors((prev) => {
              const next = new Set(prev);
              next.delete(block.id);
              return next;
            });
          }
        };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 342 }}>
              <DesktopDatePicker
                {...sharedPickerProps}
                value={block.customStartDate ?? null}
                onChange={(newValue) => {
                  updateDeliveryBlock(block.id, { customStartDate: newValue ?? null });
                  clearErrorIfBothSet(block.customEndDate);
                }}
                minDate={contractStartDate ?? undefined}
                maxDate={block.customEndDate ?? contractEndDate ?? undefined}
                shouldDisableDate={isMonths ? undefined : (d) => isDateTakenByOtherRow(block.id, d as Date)}
                shouldDisableMonth={
                  isMonths
                    ? (d) => {
                        // Disable any month that overlaps another row's range. Pass the month's
                        // first day as the test date.
                        const monthStart = startOfMonth(d as Date);
                        return isDateTakenByOtherRow(block.id, monthStart);
                      }
                    : undefined
                }
                slotProps={{ textField: { size: 'small', error: hasError, sx: datePickerSx(hasError) } }}
              />
              <Typography sx={{ color: theme.palette.text.secondary, fontSize: '14px' }}>To</Typography>
              <DesktopDatePicker
                {...sharedPickerProps}
                value={block.customEndDate ?? null}
                onChange={(newValue) => {
                  updateDeliveryBlock(block.id, { customEndDate: newValue ?? null });
                  clearErrorIfBothSet(block.customStartDate);
                }}
                minDate={block.customStartDate ?? lineItemStartDate ?? undefined}
                maxDate={contractEndDate ?? undefined}
                shouldDisableDate={isMonths ? undefined : (d) => isDateTakenByOtherRow(block.id, d as Date)}
                shouldDisableMonth={
                  isMonths
                    ? (d) => {
                        const monthStart = startOfMonth(d as Date);
                        return isDateTakenByOtherRow(block.id, monthStart);
                      }
                    : undefined
                }
                slotProps={{ textField: { size: 'small', error: hasError, sx: datePickerSx(hasError) } }}
              />
            </Box>
            <Box sx={{ width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {rowHasGap && (
                <Tooltip title="There's a time gap between this delivery period and the next." arrow placement="top">
                  <span style={{ display: 'flex', alignItems: 'center', color: theme.palette.warning.main }}>
                    <FontAwesomeIcon icon={faCircleInfo} />
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'regularImpressions',
      headerName: 'Impression Budget',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const { block } = params.row as { block: DeliveryBlock };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <TextField
              value={block.regularImpressions ? addCommas(block.regularImpressions) : ''}
              onChange={(e) => handleImpressionChange(block.id, 'regularImpressions', e.target.value)}
              placeholder="0"
              size="small"
              sx={{
                flex: 1,
                ...fieldStyles,
                '& input::placeholder': { color: theme.palette.text.disabled, opacity: 1 },
              }}
              inputProps={{ style: { textAlign: 'right' } }}
            />
          </Box>
        );
      },
    },
    {
      field: 'bonusImpressions',
      headerName: 'Bonus Impressions',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const { block } = params.row as { block: DeliveryBlock };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <TextField
              value={block.bonusImpressions ? addCommas(block.bonusImpressions) : ''}
              onChange={(e) => handleImpressionChange(block.id, 'bonusImpressions', e.target.value)}
              placeholder="0"
              size="small"
              sx={{
                flex: 1,
                ...fieldStyles,
                '& input::placeholder': { color: theme.palette.text.disabled, opacity: 1 },
              }}
              inputProps={{ style: { textAlign: 'right' } }}
            />
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 56,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const { block } = params.row as { block: DeliveryBlock };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <IconButton aria-label="Remove delivery profile" onClick={() => removeDeliveryBlock(block.id)} sx={{ color: theme.palette.action.active }}>
              <FontAwesomeIcon icon={faCircleMinus} />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  // ----- Render -----
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth={false}
      PaperProps={{
        sx: { borderRadius: 2, height: '560px', width: '960px', maxWidth: '960px' },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, position: 'relative' }}
      >
        <Typography sx={{ fontSize: '18px', fontWeight: 600, color: theme.palette.text.primary }}>
          Set Custom Flighting
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            color: theme.palette.text.secondary,
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          Flight Dates: {lineItemStartDate ? format(lineItemStartDate, 'MM/dd/yyyy') : '—'} To{' '}
          {lineItemEndDate ? format(lineItemEndDate, 'MM/dd/yyyy') : '—'}
        </Typography>
        <IconButton aria-label="Close" onClick={handleClose} sx={{ color: theme.palette.action.active }}>
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Box sx={{ width: 342 }}>
              <ButtonGroup variant="outlined" sx={{ height: 32 }}>
                <Button onClick={() => handleModalTabChange(0)} variant={modalTab === 0 ? 'contained' : 'outlined'}>
                  Custom Date Range
                </Button>
                <Button onClick={() => handleModalTabChange(1)} variant={modalTab === 1 ? 'contained' : 'outlined'}>
                  Select Months
                </Button>
              </ButtonGroup>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
              {/* Spacer matching the 20px info icon slot in each row */}
              <Box sx={{ width: 20, flexShrink: 0 }} />
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: theme.palette.text.secondary }}>
                  Impression Budget
                </Typography>
                <Tooltip
                  title="Automatically distributes the remaining impressions across rows with no manual entry."
                  arrow
                  placement="top"
                >
                  <Typography
                    component="a"
                    onClick={regularAutofillDisabled ? undefined : () => splitBudgetEvenly('regularImpressions')}
                    sx={{
                      cursor: regularAutofillDisabled ? 'default' : 'pointer',
                      color: regularAutofillDisabled ? theme.palette.text.disabled : theme.palette.primary.main,
                      fontWeight: 500,
                    }}
                  >
                    Auto-fill
                  </Typography>
                </Tooltip>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: theme.palette.text.secondary }}>
                  Bonus Impressions
                </Typography>
                <Tooltip
                  title="Automatically distributes the remaining impressions across rows with no manual entry."
                  arrow
                  placement="top"
                >
                  <Typography
                    component="a"
                    onClick={bonusAutofillDisabled ? undefined : () => splitBudgetEvenly('bonusImpressions')}
                    sx={{
                      cursor: bonusAutofillDisabled ? 'default' : 'pointer',
                      color: bonusAutofillDisabled ? theme.palette.text.disabled : theme.palette.primary.main,
                      fontWeight: 500,
                    }}
                  >
                    Auto-fill
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          </Box>
          <Box sx={{ width: 72 }} />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              flex: 1,
              mb: 1,
              '& .MuiDataGrid-root': { border: 'none', backgroundColor: 'transparent' },
              '& .MuiDataGrid-columnHeaders': { display: 'none' },
              '& .MuiDataGrid-virtualScroller': { marginTop: '0 !important', overflowX: 'hidden' },
              '& .MuiDataGrid-cell': {
                padding: '0 8px',
                border: 'none',
                overflow: 'visible',
                '&:focus': { outline: 'none' },
                '&:focus-within': { outline: 'none' },
              },
              '& .MuiDataGrid-row': {
                overflow: 'visible',
                '&:hover, &.Mui-hovered': { backgroundColor: 'transparent' },
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                  '&:hover, &.Mui-hovered': { backgroundColor: 'transparent' },
                },
              },
              '& .MuiDataGrid-cell[data-field="period"]': {
                borderTop: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderLeft: `1px solid ${theme.palette.divider}`,
                borderTopLeftRadius: '4px',
                borderBottomLeftRadius: '4px',
              },
              '& .MuiDataGrid-cell[data-field="regularImpressions"]': {
                borderTop: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-cell[data-field="bonusImpressions"]': {
                borderTop: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
                borderTopRightRadius: '4px',
                borderBottomRightRadius: '4px',
              },
              '& .MuiDataGrid-footerContainer': { display: 'none' },
              '& .MuiDataGrid-filler': { display: 'none' },
            }}
          >
            <DataGrid
              rows={draftRows}
              columns={columns}
              rowHeight={56}
              getRowSpacing={() => ({ top: 4, bottom: 4 })}
              hideFooter
              disableRowSelectionOnClick
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              disableDensitySelector
              disableAggregation
              disableRowGrouping
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <IconButton aria-label="Add delivery profile" onClick={addDeliveryBlock} sx={{ color: theme.palette.action.active }}>
              <FontAwesomeIcon icon={faCirclePlus} />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: regularColor }}>
            <Typography sx={{ fontSize: '13px', color: 'inherit' }}>
              Allocated Impression Budget:{' '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {addCommas(totalRegular)}
                {'  '}({regularPct.toFixed(1)}%)
              </Box>
            </Typography>
            {modalTouched && regularPct !== 100 && (
              <Tooltip
                title={
                  regularPct > 100
                    ? 'Allocated impression budget exceeds 100%. Please adjust your values.'
                    : 'Allocated impression budget is under 100%.'
                }
                arrow
                placement="top"
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FontAwesomeIcon icon={faCircleExclamation} />
                </Box>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: bonusColor }}>
            <Typography sx={{ fontSize: '13px', color: 'inherit' }}>
              Allocated Bonus Impressions:{' '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {addCommas(totalBonus)}
                {'  '}({bonusPct.toFixed(1)}%)
              </Box>
            </Typography>
            {modalTouched && bonusPct !== 100 && (
              <Tooltip
                title={
                  bonusPct > 100
                    ? 'Allocated bonus impressions exceed 100%. Please adjust your values.'
                    : 'Allocated bonus impressions are under 100%.'
                }
                arrow
                placement="top"
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FontAwesomeIcon icon={faCircleExclamation} />
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSetDelivery}
          disabled={isOverLimit}
          sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}
        >
          Set Delivery
        </Button>
        <Box sx={{ flex: 1 }} />
      </DialogActions>
    </Dialog>
  );
}

export default CustomDeliveryProfileModal;
